import { useEffect, useState } from "react";
import { Plus, Copy, Trash2, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Field, Modal, Empty } from "@/components/common";

const SAMPLE = { first_name: "Li", full_name: "Li Wei", company: "Bright LED Co.", event_name: "Canton Fair 2026", product_interest: "LED panels", sender_name: "Your name", sender_business: "Your business", sender_website: "https://example.com" };

export default function Templates() {
  const [items, setItems] = useState([]);
  const [vars, setVars] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/templates").then((r) => { setItems(r.data.items); setVars(r.data.variables); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (edit.id) await api.put(`/templates/${edit.id}`, edit); else await api.post("/templates", edit);
      toast.success("Template saved"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dup = async (t) => { await api.post(`/templates/${t.id}/duplicate`); load(); };
  const del = async (t) => { if (window.confirm(`Delete “${t.name}”?`)) { await api.delete(`/templates/${t.id}`); load(); } };
  const insert = (v) => setEdit({ ...edit, body: `${edit.body}{${v}}` });
  const preview = (s) => (s || "").replace(/\{([a-z_]+)\}/g, (m, k) => SAMPLE[k] || m);

  return (
    <div>
      <PageHeader title="Templates" subtitle="Template-only mode substitutes variables exactly. AI mode uses a template as guidance."
        actions={<button data-testid="template-new-btn" onClick={() => setEdit({ name: "", subject: "", body: "" })} className="cd-btn-primary"><Plus className="h-4 w-4" />New template</button>} />
      {items.length === 0 && <Empty icon={FileText} title="No templates" />}
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((t) => (
          <div key={t.id} className="cd-card" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="font-bold">{t.name}</p><p className="text-sm text-slate-500">{t.subject}</p></div>
              {t.is_sample && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Starter</span>}
            </div>
            <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-slate-600">{t.body}</p>
            <div className="mt-3 flex gap-1">
              <button data-testid={`template-edit-${t.id}`} onClick={() => setEdit(t)} className="cd-btn-ghost h-9 px-3"><Pencil className="h-4 w-4" />Edit</button>
              <button data-testid={`template-duplicate-${t.id}`} onClick={() => dup(t)} className="cd-btn-ghost h-9 px-3"><Copy className="h-4 w-4" />Duplicate</button>
              <button data-testid={`template-delete-${t.id}`} onClick={() => del(t)} className="cd-btn-ghost h-9 px-3 text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <Modal open onClose={() => setEdit(null)} title={edit.id ? "Edit template" : "New template"} wide testid="template-dialog">
          <div className="space-y-3">
            <Field label="Name"><input data-testid="template-name-input" className="cd-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Subject"><input data-testid="template-subject-input" className="cd-input" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} /></Field>
            <Field label="Body"><textarea data-testid="template-body-textarea" rows={10} className="cd-textarea" value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} /></Field>
            <div className="flex flex-wrap gap-1">{vars.map((v) => <button key={v} data-testid={`template-var-${v}`} onClick={() => insert(v)} className="rounded-lg bg-teal-50 px-2 py-1 font-mono text-xs text-teal-800">{`{${v}}`}</button>)}</div>
            <p className="text-xs text-slate-500">Missing values fall back gracefully (e.g. “Hi there”, “your company”). Missing sender details block sending until filled in.</p>
            <div className="rounded-xl bg-slate-50 p-3 text-sm"><p className="cd-label">Preview with sample data</p><p className="font-semibold">{preview(edit.subject)}</p><p className="mt-1 whitespace-pre-wrap">{preview(edit.body)}</p></div>
            <button data-testid="template-save-btn" disabled={!edit.name || !edit.subject || !edit.body} onClick={save} className="cd-btn-primary w-full">Save template</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
